import { parsePhoneNumberFromString } from 'libphonenumber-js';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - no types for this version of mixpanel-browser
import mixpanel from 'mixpanel-browser';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  addToCart,
  clearOptionalIncentiveToConfigure,
  updateSMSNumberConfirmed,
} from '../../../../actions/v3Order';
import {
  isProductAllowed,
  isShippingRestrictedForParent,
} from '../../helpers/internationalRestriction';
import BackLinkFromProduct from '../../shared/BackLinkFromProduct.jsx';
import ProductHeader from './subcomponents/ProductHeader';
import ProductSubheader from './subcomponents/ProductSubheader';
import ProductConfigurationSteps from './subcomponents/ProductConfigurationSteps';
import ProductInfoPanel from './subcomponents/ProductInfoPanel';
import {
  useIsIncentiveFlow,
  useIsInterstitialFlow,
  useIsPreCartOfferFlow,
} from '../../../../hooks/useOrderFlowContext';
import {
  autoOpenConfigPanel,
  resetConfig,
  setCurrentProduct,
  setEditMode,
  setPersonalizationOffered,
  setPersonalizationValue,
} from '../../../../reducers/productConfigSlice';
import { selectShowCropOptions } from '../../../../selectors';

interface RegularProductConfigProps {
  product: any;
  hideBackLink?: boolean;
  redirectSource?: string;
  actionFooter?: React.ReactNode;
}

const RegularProductConfig = ({
  product,
  hideBackLink,
  redirectSource,
  actionFooter,
}: RegularProductConfigProps) => {
  const { control, handleSubmit, register, setValue } = useForm();
  const dispatch = useDispatch();

  const backgroundSelections = useSelector(
    (state: any) => state.productConfig.backgroundSelections,
  );
  const poseSelections = useSelector(
    (state: any) => state.productConfig.poseSelections,
  );
  const poseSelectionIds = useSelector(
    (state: any) => state.productConfig.poseSelectionIds,
  );
  const cropSelections = useSelector(
    (state: any) => state.productConfig.cropSelections,
  );
  const personalizationValue = useSelector(
    (state: any) => state.productConfig.personalizationValue,
  );
  const selectedProductOptionKey = useSelector(
    (state: any) => state.productConfig.selectedProductOptionKey,
  );
  const selectedProductOptions = useSelector(
    (state: any) => state.productConfig.selectedProductOptions,
  );
  // Flow context (incentive/interstitial/pre-cart) is derived from the
  // route — see hooks/useOrderFlowContext.
  const isIncentiveProduct = useIsIncentiveFlow();
  const isInterstitialProduct = useIsInterstitialFlow();
  const isPreCartOfferProduct = useIsPreCartOfferFlow();

  const { selectedStudent } = useSelector((state: any) => state.additionalData);
  const { phone, smsPhone } = useSelector((state: any) => state.parent);
  const smsNumberConfirmed = useSelector(
    (state: any) => state.v3Order.smsNumberConfirmed,
  );
  const parent = useSelector((state: any) => state.parent);
  const shoot = useSelector((state: any) => state.shoot.shoot);
  const shippingRestricted = isShippingRestrictedForParent(shoot, parent);

  const showCropOptions = useSelector(selectShowCropOptions);

  // Reset config and personalization state when product changes. Whether
  // personalization is offered at all is derived from shoot state
  // (selectShouldOfferPersonalization) and flow context from the route
  // (useIsIncentiveFlow / useIsInterstitialFlow) — not stored here.
  // Flow-specific behavior (incentive picker seeding, interstitial
  // announcement) lives in the route wrappers, whose effects run after this
  // one (child effects run before parent effects) — this reset must stay in
  // an effect for that ordering to hold.
  useEffect(() => {
    dispatch(setCurrentProduct(product));

    dispatch(setPersonalizationOffered(false));
    dispatch(setPersonalizationValue(null));
    dispatch(resetConfig());

    // Desktop-only auto-open of the config panel (hidden by default) — the
    // thunk reads post-reset state, so it decides against THIS product's
    // clean slate. On mobile every product starts closed behind the
    // SelectedPosesAndBackgrounds "Tap to configure" entry point — including
    // skip-background digital downloads: the entry point renders for them
    // whenever poses are required (ENG-2300), so the ENG-2542
    // every-screen-size auto-open is no longer the only way into their
    // required pose step.
    if (window.innerWidth > 991) {
      (dispatch as any)(autoOpenConfigPanel());
    }
  }, [product, dispatch]);

  const productRequiresSMS = product.sms_required;

  const handleConfirmSMS = async () => {
    const phoneNumberObject = parsePhoneNumberFromString(
      smsPhone || phone,
      'US',
    );

    if (!phoneNumberObject || !phoneNumberObject.isValid()) {
      Swal.fire('Invalid Phone Number', '', 'error');
      return;
    }
    const result = await Swal.fire({
      title: 'Please Confirm',
      text: `Please confirm ${phoneNumberObject.formatNational()} is the mobile number where we should send your SMS-based products`,
      confirmButtonText: 'OK',
      showCancelButton: true,
      reverseButtons: true,
      focusConfirm: true,
      cancelButtonText: 'Edit',
    });

    if (result.value === true) {
      // updateSMSNumberConfirmed is a thunk; cast needed until store is typed
      (dispatch as any)(updateSMSNumberConfirmed(true));
      Swal.fire(
        'Confirmed',
        'You may now add the product to your cart',
        'success',
      );
    } else if (result.dismiss === Swal.DismissReason.cancel) {
      dispatch(setEditMode(true));
    }
  };

  const addProductToCart = () => {
    handleSubmit((formState) => {
      if (!smsNumberConfirmed && productRequiresSMS) {
        handleConfirmSMS();
      } else {
        const backgroundIds = backgroundSelections.map(
          (background: any) => background.id,
        );

        if (isPreCartOfferProduct) {
          mixpanel.track('Pre-Cart Offer Button Click', {
            Selection: 'Add to Cart',
          });
        }
        // addToCart is a thunk; cast needed until store is typed
        (dispatch as any)(
          addToCart({
            studentId: selectedStudent.id,
            fullproductData: product,
            backgroundSelections,
            backgroundIds,
            poseIds: poseSelectionIds,
            poses: poseSelections,
            isInterstitialProduct,
            quantity: 1,
            personalizationValue:
              personalizationValue != null ? personalizationValue.trim() : null,
            redirectSource: (redirectSource ?? null) as any,
            requiredFields: formState,
            legacyRequiredFields: formState,
            productOptionKey: selectedProductOptionKey,
            productOptions: selectedProductOptions,
            selectedCropOptions: showCropOptions
              ? cropSelections.filter(Boolean)
              : [],
          }),
        );
        if (isIncentiveProduct) {
          // Belt and braces: the v3Order reducer already clears a marker
          // matching this add, in the same dispatch, and the middleware routes
          // off the add's own `optionalIncentiveReAdd` payload rather than the
          // marker (ENG-2043 R7) — so nothing downstream depends on this. It
          // stays as a guard for a marker left pinned to some other product.
          dispatch(clearOptionalIncentiveToConfigure());
        }
      }
    })();
  };

  if (shippingRestricted && !isProductAllowed(product, true)) {
    return <Redirect to="/v3/offers" />;
  }

  return (
    <div id="v3-regular-product-config">
      <div className="bnl-product-config">
        <BackLinkFromProduct
          hidden={hideBackLink || isIncentiveProduct || isInterstitialProduct}
        />
        <ProductHeader product={product} />
        <ProductSubheader product={product} />
        <div className="row">
          <div className="bnl-product-config__product-info col-xs-12 col-md-4 col-lg-4">
            <ProductInfoPanel
              actionFooter={actionFooter}
              addProductToCart={addProductToCart}
            />
          </div>
          <div className="bnl-product-config__step col-xs-12 col-md-8 col-lg-8">
            <ProductConfigurationSteps
              addProductToCart={addProductToCart}
              control={control}
              register={register}
              setValue={setValue}
              actionFooter={actionFooter}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegularProductConfig;
